import { useCallback, useEffect, useRef, useState } from "react";
import { ZodError } from "zod";
import { PhonesHTTPError } from "../../lib/api/endpoints";
import {
  AddEditPhoneResponse,
  DateString,
  PhoneContact,
  PhoneOwnerType,
  PhonesConsentSourceType,
  PhonesConsentStatus,
  PhonesInactivationDelivery,
  PhonesInactivationSource,
  PhonesRestrictionFrequency,
  PhoneSuffix,
  PhoneType,
  PhoneUpdateFiservType,
  ValidationErrors,
} from "../../lib/api/models";
import { useAddPhone, useEditPhone } from "../../lib/api/queries";
import { formatToDateStringInCentralTime } from "../../lib/formatting";
import { setValidationErrorsAndDisplayGeneralErrors } from "../../lib/validation";
import { addEditPhoneRequestSchema } from "../../lib/validation/schemas";

const strings = {
  duplicatePhone:
    "The telephone number you entered is already associated with the card member.",
};

const EMPTY_FISERV_NUMBERS = [
  "0000000000",
  "4444444444",
  "7777777777",
  "8888888888",
  "5555555555",
  "9990000000",
];

export enum PhoneNumberOverride {
  ConsentStatus = "consentStatusOverride",
  UpdatePhoneNumber = "updatePhoneNumberOverride",
}

const EMPTY_OVERRIDES = {
  [PhoneNumberOverride.ConsentStatus]: false,
  [PhoneNumberOverride.UpdatePhoneNumber]: false,
};

type DuplicatePhoneValidationError = {
  message: string;
  phoneContact: PhoneContact;
};

export type PhoneNumberFormInput = {
  phoneId: string;
  phoneNumber: string;
  extension: string;
  ownerType: PhoneOwnerType | undefined;
  ownerFirstName: string;
  ownerLastName: string;
  ownerSuffix: PhoneSuffix | null;
  phoneType: PhoneType | undefined;
  updateFiservCode: PhoneUpdateFiservType;
  consentStatus: PhonesConsentStatus | undefined;
  consentSourceType: PhonesConsentSourceType | undefined;
  verified: boolean;
  restricted: boolean;
  restrictionDays: string[];
  restrictionLiftDate: DateString | null;
  restrictionFrequency: PhonesRestrictionFrequency;
  active: boolean;
  inactivationReasonId: string | undefined;
  reactivationDate: DateString | null;
  inactivationDeliveryType: PhonesInactivationDelivery | null | undefined;
  inactivationSourceType: PhonesInactivationSource | null | undefined;
  memo: string;
  isDirty: boolean;
  isComplete: boolean;
};

const getDefaultRestrictionLiftDate = (): DateString => {
  const now = new Date();

  return formatToDateStringInCentralTime(
    new Date(now.getFullYear() + 50, now.getMonth(), now.getDate()),
  );
};

const INITIAL_PHONE_NUMBER_FORM_STATE: PhoneNumberFormInput = {
  phoneId: "",
  phoneNumber: "",
  extension: "",
  ownerType: undefined,
  ownerFirstName: "",
  ownerLastName: "",
  ownerSuffix: null,
  phoneType: undefined,
  updateFiservCode: "UPDATE_NEITHER",
  consentStatus: undefined,
  consentSourceType: undefined,
  verified: false,
  restricted: false,
  restrictionDays: [],
  restrictionLiftDate: getDefaultRestrictionLiftDate(),
  restrictionFrequency: "NONE",
  active: true,
  inactivationReasonId: undefined,
  reactivationDate: null,
  inactivationDeliveryType: undefined,
  inactivationSourceType: undefined,
  memo: "",
  isDirty: false,
  isComplete: false,
};

const formHasChanged = (
  formState: PhoneNumberFormInput,
  initialFormState: PhoneNumberFormInput,
) =>
  formState.restrictionDays.toSorted().join() !==
    initialFormState.restrictionDays.toSorted().join() ||
  (Object.keys(formState) as Array<keyof PhoneNumberFormInput>).some(
    (key) =>
      key !== "isComplete" &&
      key !== "isDirty" &&
      key !== "restrictionDays" &&
      formState[key] !== initialFormState[key],
  );

export const isConsentStatusRequired = (
  formState: PhoneNumberFormInput,
  formType: "ADD" | "EDIT",
  prevConsentStatus: PhonesConsentStatus | null | undefined,
  prevActive: boolean,
) =>
  (formType === "ADD" && formState.active) ||
  (formType === "EDIT" &&
    prevConsentStatus !== "PEC_YES" &&
    !prevActive &&
    formState.active);

const isFormComplete = (
  formState: PhoneNumberFormInput,
  phoneContact: PhoneContact | undefined,
): boolean =>
  !!formState.phoneNumber &&
  (!!formState.consentStatus ||
    !isConsentStatusRequired(
      formState,
      phoneContact ? "EDIT" : "ADD",
      phoneContact?.consentStatus,
      phoneContact?.inactivation === null,
    )) &&
  (!formState.consentStatus || !!formState.consentSourceType) &&
  (!formState.restricted || formState.restrictionLiftDate !== "") &&
  (formState.active ||
    !formState.inactivationReasonId ||
    (formState.inactivationDeliveryType !== undefined &&
      formState.inactivationSourceType !== undefined));

const getInitialFormState = (
  phoneContact?: PhoneContact,
): PhoneNumberFormInput => {
  if (!phoneContact) {
    return INITIAL_PHONE_NUMBER_FORM_STATE;
  }

  return {
    phoneId: phoneContact.phoneID,
    phoneNumber: phoneContact.phoneNumber,
    extension: phoneContact.extension,
    ownerType: phoneContact.ownerType,
    ownerFirstName: phoneContact.ownerFirstName,
    ownerLastName: phoneContact.ownerLastName,
    ownerSuffix: phoneContact.ownerSuffix,
    phoneType: phoneContact.phoneType,
    updateFiservCode: "UPDATE_NEITHER",
    consentStatus:
      phoneContact.inactivation !== null && phoneContact.consentStatus !== null
        ? phoneContact.consentStatus
        : undefined,
    consentSourceType: undefined,
    verified: false,
    restricted: phoneContact.restriction !== null,
    restrictionDays: phoneContact.restriction?.restrictionDays ?? [],
    restrictionLiftDate: !phoneContact.restriction
      ? getDefaultRestrictionLiftDate()
      : phoneContact.restriction.restrictionLiftDate
        ? formatToDateStringInCentralTime(
            phoneContact.restriction.restrictionLiftDate,
          )
        : null,
    restrictionFrequency:
      phoneContact.restriction?.restrictionFrequency ?? "NONE",
    active: phoneContact.inactivation === null,
    inactivationReasonId: phoneContact.inactivation?.inactivationReasonId,
    reactivationDate: phoneContact.inactivation?.reactivationDate
      ? formatToDateStringInCentralTime(
          phoneContact.inactivation?.reactivationDate,
        )
      : null,
    inactivationDeliveryType:
      phoneContact.inactivation?.inactivationDeliveryType,
    inactivationSourceType: phoneContact.inactivation?.inactivationSourceType,
    memo: "",
    isDirty: false,
    isComplete: false,
  };
};

export const usePhoneNumberForm = (
  dwbuid: string,
  partyId: string,
  onSuccess: () => void,
  onDisplayGeneralErrors: (err: unknown) => void,
  phoneContact?: PhoneContact,
  referenceId?: string,
  recordOfExistingPhoneContactsByPhoneNumber?: Record<string, PhoneContact>,
): [
  PhoneNumberFormInput,
  {
    generalDispatch: (payload: Partial<PhoneNumberFormInput>) => void;
    validationMessages?: ValidationErrors;
    setValidationMessages: React.Dispatch<
      React.SetStateAction<ValidationErrors | undefined>
    >;
    isLoading: boolean;
    checkOverridesAndSubmit: (
      primaryNumber: string,
      secondaryNumber: string,
    ) => PhoneNumberOverride | undefined;
    resetForm: () => void;
    partialSuccessMessages: string[];
    onClosePartialSuccessMessages: () => void;
    addOverride: (override: PhoneNumberOverride) => void;
    clearOverrides: () => void;
    duplicatePhoneValidationError: DuplicatePhoneValidationError | undefined;
  },
] => {
  const initialFormState = useRef(getInitialFormState(phoneContact));

  const [formState, formDispatch] = useState<PhoneNumberFormInput>(
    initialFormState.current,
  );
  const [validationMessages, setValidationMessages] =
    useState<ValidationErrors>();
  const [partialSuccessMessages, setPartialSuccessMessages] = useState<
    string[]
  >([]);
  const [duplicatePhoneValidationError, setDuplicatePhoneValidationError] =
    useState<DuplicatePhoneValidationError>();

  const overrides = useRef(EMPTY_OVERRIDES);

  const addPhoneMutation = useAddPhone(dwbuid);
  const editPhoneMutation = useEditPhone(dwbuid);
  const isLoading = addPhoneMutation.isLoading || editPhoneMutation.isLoading;

  useEffect(() => {
    initialFormState.current = getInitialFormState(phoneContact);
  }, [phoneContact]);

  const clearValidationMessages = useCallback((...keys: string[]) => {
    setValidationMessages((prev) => {
      const next = { ...prev };
      keys.forEach((key) => {
        next[key] = [];
      });

      return next;
    });
  }, []);

  const generalDispatch = useCallback(
    (payload: Partial<PhoneNumberFormInput>) => {
      const payloadKeys = Object.keys(payload);
      clearValidationMessages(...payloadKeys);

      // clear existing phone contact validation
      if (
        payloadKeys.includes("phoneNumber") ||
        payloadKeys.includes("extension")
      ) {
        setDuplicatePhoneValidationError(undefined);
      }

      formDispatch((prevFormState) => {
        const newFormState = {
          ...prevFormState,
          ...payload,
        };

        return {
          ...newFormState,
          isDirty: formHasChanged(newFormState, initialFormState.current),
          isComplete: isFormComplete(newFormState, phoneContact),
        };
      });
    },
    [clearValidationMessages, phoneContact],
  );

  const resetForm = useCallback(() => {
    formDispatch(initialFormState.current);
    setValidationMessages(undefined);
    setDuplicatePhoneValidationError(undefined);
  }, []);

  const checkForPartialSuccessMessages = (
    addEditPhoneResponse: AddEditPhoneResponse,
  ) => {
    if (
      addEditPhoneResponse.partialSuccessMessages &&
      addEditPhoneResponse.partialSuccessMessages.length > 0
    ) {
      setPartialSuccessMessages(addEditPhoneResponse.partialSuccessMessages);
    } else {
      onSuccess();
    }
  };

  const onClosePartialSuccessMessages = () => {
    setPartialSuccessMessages([]);
    onSuccess();
  };

  const addOverride = (override: PhoneNumberOverride) => {
    overrides.current = { ...overrides.current, [override]: true };
  };

  const clearOverrides = () => {
    overrides.current = EMPTY_OVERRIDES;
  };

  const onSubmit = () => {
    // when adding number, check if number existing
    let currExistingPhoneContact: PhoneContact | undefined;
    if (recordOfExistingPhoneContactsByPhoneNumber) {
      currExistingPhoneContact =
        recordOfExistingPhoneContactsByPhoneNumber[
          `${formState.phoneNumber}-${formState.extension}`
        ];
      setDuplicatePhoneValidationError(
        currExistingPhoneContact
          ? {
              message: strings.duplicatePhone,
              phoneContact: currExistingPhoneContact,
            }
          : undefined,
      );
    }

    // validate locally before submit
    try {
      addEditPhoneRequestSchema.parse(formState);
    } catch (err) {
      if (err instanceof ZodError && err.errors.length > 0) {
        const valMessages: ValidationErrors = {};
        err.errors.forEach((vm) => (valMessages[vm.path[0]] = [vm.message]));
        setValidationMessages(valMessages);
        return;
      }
    }

    // disallow submit and return with validation
    if (currExistingPhoneContact) {
      return;
    }

    setValidationMessages({});

    if (!formState.ownerType || !formState.phoneType) {
      return;
    }

    let updateFiservCode = formState.updateFiservCode;

    let restriction = null;
    if (formState.restricted) {
      if (
        !formState.restrictionFrequency ||
        formState.restrictionLiftDate === ""
      ) {
        return;
      }
      restriction = {
        restrictionDays: formState.restrictionDays,
        restrictionFrequency: formState.restrictionFrequency,
        restrictionLiftDate: formState.restrictionLiftDate,
      };
    }

    let inactivation = null;
    if (!formState.active) {
      if (
        !formState.inactivationReasonId ||
        formState.reactivationDate === "" ||
        formState.inactivationSourceType === undefined ||
        formState.inactivationDeliveryType === undefined
      ) {
        return;
      }
      inactivation = {
        inactivationReasonId: formState.inactivationReasonId,
        reactivationDate: formState.reactivationDate
          ? formState.reactivationDate
          : null,
        inactivationSourceType: formState.inactivationSourceType,
        inactivationDeliveryType: formState.inactivationDeliveryType,
      };
      // pass in neither if adding/editing inactive
      updateFiservCode = "UPDATE_NEITHER";
    }

    if (phoneContact === undefined) {
      addPhoneMutation
        .mutateAsync({
          partyId,
          dwbuid,
          phoneNumber: formState.phoneNumber,
          extension: formState.extension,
          ownerType: formState.ownerType,
          ownerFirstName: formState.ownerFirstName,
          ownerLastName: formState.ownerLastName,
          ownerSuffix: formState.ownerSuffix,
          phoneType: formState.phoneType,
          updateFiservCode,
          consentStatus:
            formState.consentStatus ?? "PEC_NOT_SPECIFIED_OR_UNKNOWN",
          consentSourceType: formState.consentSourceType ?? "OTHER",
          verified: formState.verified,
          memo: formState.memo,
          restriction,
          inactivation,
          referenceId,
        })
        .then((res: AddEditPhoneResponse) =>
          checkForPartialSuccessMessages(res),
        )
        .catch((err) => {
          setValidationErrorsAndDisplayGeneralErrors(
            err,
            setValidationMessages,
            onDisplayGeneralErrors,
          );

          if (err instanceof PhonesHTTPError) {
            if (err.validation?.updateConsentStatus) {
              generalDispatch({
                consentStatus: err.validation.updateConsentStatus,
              });
            }
            if (err.validation?.updatePhoneType) {
              generalDispatch({ phoneType: err.validation.updatePhoneType });
            }
          }
        });
    } else {
      const hasConsentChanged =
        formState.consentStatus !== initialFormState.current.consentStatus ||
        formState.consentSourceType !==
          initialFormState.current.consentSourceType;

      editPhoneMutation
        .mutateAsync({
          partyId: partyId,
          dwbuid: dwbuid,
          phoneId: formState.phoneId,
          phoneNumber: phoneContact.phoneNumber, // todo: can replace this with formState.phoneNumber once we have it
          extension: formState.extension,
          ownerType: formState.ownerType,
          ownerFirstName: formState.ownerFirstName,
          ownerLastName: formState.ownerLastName,
          ownerSuffix: formState.ownerSuffix,
          phoneType: formState.phoneType,
          updateFiservCode,
          // If consent has not changed, send null, otherwise send value
          consentStatus: hasConsentChanged
            ? (formState.consentStatus ??
              phoneContact.consentStatus ??
              "PEC_NOT_SPECIFIED_OR_UNKNOWN")
            : null,
          // If consent has not changed, send null, otherwise send value
          consentSourceType: hasConsentChanged
            ? (formState.consentSourceType ??
              phoneContact.consentSourceType ??
              "OTHER")
            : null,
          restriction,
          inactivation,
          memo: formState.memo,
          referenceId,
        })
        .then((res: AddEditPhoneResponse) =>
          checkForPartialSuccessMessages(res),
        )
        .catch((err) => {
          setValidationErrorsAndDisplayGeneralErrors(
            err,
            setValidationMessages,
            onDisplayGeneralErrors,
          );

          if (err instanceof PhonesHTTPError) {
            if (err.validation?.updateConsentStatus) {
              generalDispatch({
                consentStatus: err.validation.updateConsentStatus,
              });
            }
            if (err.validation?.updatePhoneType) {
              generalDispatch({ phoneType: err.validation.updatePhoneType });
            }
          }

          clearOverrides();
        });
    }
  };

  const checkOverridesAndSubmit = (
    primaryNumber: string,
    secondaryNumber: string,
  ): PhoneNumberOverride | undefined => {
    if (
      !overrides.current[PhoneNumberOverride.ConsentStatus] &&
      (formState.consentStatus === "PEC_NO" ||
        (!formState.active && formState.consentStatus === "PEC_YES"))
    ) {
      return PhoneNumberOverride.ConsentStatus;
    } else if (
      !overrides.current[PhoneNumberOverride.UpdatePhoneNumber] &&
      formState.active &&
      !formState.restricted &&
      ((formState.updateFiservCode === "UPDATE_FDR_HOME" &&
        EMPTY_FISERV_NUMBERS.some((v) => v === primaryNumber)) ||
        (formState.updateFiservCode === "UPDATE_FDR_WORK" &&
          EMPTY_FISERV_NUMBERS.some((v) => v === secondaryNumber)))
    ) {
      return PhoneNumberOverride.UpdatePhoneNumber;
    } else {
      onSubmit();
    }
  };

  return [
    formState,
    {
      generalDispatch,
      validationMessages,
      setValidationMessages,
      isLoading,
      checkOverridesAndSubmit,
      resetForm,
      partialSuccessMessages,
      onClosePartialSuccessMessages,
      addOverride,
      clearOverrides,
      duplicatePhoneValidationError,
    },
  ];
};
